import { useState, useMemo, useEffect } from "react";
import { useApp } from "../../store.jsx";
import {
  Icon,
  Avatar,
  Pagination,
  EmptyState,
  SkeletonTable,
} from "../../components/Common.jsx";
import { AddEmployeeModal, BulkInviteModal } from "../../components/Modals.jsx";
import {
  listEmployees,
  downloadEmployeesExport,
  updateEmployee as apiUpdateEmployee,
  deleteEmployee as apiDeleteEmployee,
  listDepartments,
  listReports,
  getReportById,
  sendBulkReminder,
  bulkDeactivateEmployees,
} from "../../lib/api";
import { ReportModal } from "../../components/Modals.jsx";

export default function Employees() {
  const { state, openModal, closeModal, toast, pageContext } =
    useApp();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState(
    pageContext?.department || "all",
  );
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [filter, setFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("name");
  const [dir, setDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleSelectAll = () => {
    if (paged.every((e) => selectedIds.includes(e.id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paged.map((e) => e.id));
    }
  };
  const clearSelection = () => setSelectedIds([]);
  const isSelected = (id) => selectedIds.includes(id);
  const selectedCount = selectedIds.length;

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ employees: rows }, depts] = await Promise.all([
        listEmployees({ from: dateFrom || undefined, to: dateTo || undefined }),
        listDepartments().catch(() => []),
      ]);
      // Only show actual employees on this tab (owners, admins, managers
      // appear on their respective tabs).
      const onlyEmployees = (rows || []).filter((u) => u.role === "Employee");
      setEmployees(onlyEmployees);
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (e) {
      toast(e.message || "Could not load employees", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    let r = [...employees];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(
        (e) =>
          (e.name || "").toLowerCase().includes(q) ||
          (e.email || "").toLowerCase().includes(q) ||
          (e.department || "").toLowerCase().includes(q),
      );
    }
    if (departmentFilter !== "all") {
      r = r.filter((e) => (e.department || "") === departmentFilter);
    }
    if (filter === "active") r = r.filter((e) => e.status === "active");
    else if (filter === "inactive") r = r.filter((e) => e.status === "inactive");
    else if (filter === "whatsapp") r = r.filter((e) => e.whatsapp);
    r.sort((a, b) => {
      const av = a[sort];
      const bv = b[sort];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [employees, search, filter, departmentFilter, sort, dir]);

  const total = rows.length;
  const start = (page - 1) * pageSize;
  const paged = rows.slice(start, start + pageSize);
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const sortBy = (col) => {
    if (sort === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(col);
      setDir("asc");
    }
  };

  const openEditForm = (id) => {
    const e = employees.find((x) => x.id === id);
    if (!e) return;
    openModal(
      <EditEmployeeModal
        employee={e}
        onClose={closeModal}
        onSaved={() => { closeModal(); refresh(); }}
        toast={toast}
      />,
      "lg",
    );
  };

  const openReports = (emp) => {
    openModal(
      <EmployeeReportsModal emp={emp} onClose={closeModal} toast={toast} />,
      "lg",
    );
  };

  const openInvite = () =>
    openModal(
      <AddEmployeeModal
        onClose={closeModal}
        onSubmit={(result) => {
          toast(`Invitation sent to ${result?.invitation?.email}`, "success");
          refresh();
        }}
      />,
      "md",
    );

  const onExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadEmployeesExport({
        department: departmentFilter !== "all" ? departmentFilter : undefined,
        status: filter !== "all" ? filter : undefined,
        search: search || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
      });
      toast("Employees CSV downloaded", "success");
    } catch (e) {
      toast(e.message || "Export failed", "error");
    } finally {
      setExporting(false);
    }
  };

  const setFilterAndReset = (k) => {
    setFilter(k);
    setPage(1);
  };

  const departmentsCount = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => e.department && set.add(e.department));
    return set.size || state.departments?.length || 0;
  }, [employees, state.departments]);

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-sub">
            {total} team members across {departmentsCount} departments
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onExport}
            disabled={exporting || !employees.length}
          >
            <Icon name="download" size={16} />{" "}
            {exporting ? "Exporting…" : "Export"}
          </button>
          <button className="btn btn-primary btn-sm" onClick={openInvite}>
            <Icon name="user-plus" size={16} /> Invite Employee
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() =>
            openModal(
              <BulkInviteModal
                onClose={closeModal}
                onSubmit={(result) => {
                  toast(`Invited ${result.results.length} employee(s)`, "success");
                  refresh();
                }}
                toast={toast}
              />,
              "md",
            )
          }>
            <Icon name="upload" size={16} /> Bulk Invite
          </button>
        </div>
      </div>
      <div className="table-toolbar">
        <div className="toolbar-left">
          <div className="toolbar-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              placeholder="Search by name, email, dept..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {[
            ["all", "All"],
            ["active", "Active"],
            ["inactive", "Inactive"],
            ["whatsapp", "WhatsApp connected"],
          ].map(([k, l]) => (
            <button
              key={k}
              className={`filter-chip ${filter === k ? "active" : ""}`}
              onClick={() => setFilterAndReset(k)}
            >
              {l}
            </button>
          ))}
          <select
            className="input"
            style={{ height: 30, padding: "0 8px", width: 140, fontSize: 13 }}
            value={departmentFilter}
            onChange={(e) => {
              setDepartmentFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All depts</option>
            {departments.map((d) => (
              <option key={d.id || d.name} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
          {(dateFrom || dateTo) && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}>Clear</button>
          )}
        </div>
      </div>
      {selectedCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            marginBottom: 8,
            background: "var(--primary-1)",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {selectedCount} selected
          </span>
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            <button
              className="btn btn-sm btn-ghost"
              onClick={async () => {
                try {
                  await sendBulkReminder(selectedIds);
                  toast(`Reminder sent to ${selectedCount} employee(s)`, "success");
                  clearSelection();
                } catch (err) {
                  toast(err.message || "Failed to send reminders", "error");
                }
              }}
              title="Send WhatsApp reminder to selected employees"
            >
              <Icon name="bell" size={14} /> Send Reminder
            </button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => {
                const selected = employees.filter((e) => selectedIds.includes(e.id));
                const escape = (v) => {
                  if (v == null) return "";
                  const s = String(v);
                  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                };
                const header = ["Name", "Email", "Department", "Role", "Status", "WhatsApp", "Reports", "Compliance"];
                const rows = selected.map((e) =>
                  [e.name, e.email, e.department, e.role, e.status, e.whatsapp ? "yes" : "no", e.reportsCount, e.compliance]
                    .map(escape).join(","),
                );
                const csv = "\uFEFF" + [header.join(","), ...rows].join("\r\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `selected-employees-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast(`Exported ${selected.length} employee(s)`, "success");
              }}
              title="Export selected employees as CSV"
            >
              <Icon name="download" size={14} /> Export
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={async () => {
                if (!window.confirm(`Deactivate ${selectedCount} employee(s)? They will no longer be able to submit reports.`)) return;
                try {
                  await bulkDeactivateEmployees(selectedIds);
                  toast(`Deactivated ${selectedCount} employee(s)`, "success");
                  clearSelection();
                  refresh();
                } catch (err) {
                  toast(err.message || "Failed to deactivate", "error");
                }
              }}
              title="Deactivate selected employees"
            >
              <Icon name="user-x" size={14} /> Deactivate
            </button>
            <button className="btn btn-sm btn-ghost" onClick={clearSelection}>
              <Icon name="x" size={14} /> Clear
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <SkeletonTable rows={6} cols={5} />
      ) : paged.length ? (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={paged.length > 0 && paged.every((e) => isSelected(e.id))}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th onClick={() => sortBy("name")}>
                  Employee{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th onClick={() => sortBy("department")}>
                  Department{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th onClick={() => sortBy("role")}>
                  Role{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th>WhatsApp</th>
                <th onClick={() => sortBy("reportsCount")}>
                  Reports{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th onClick={() => sortBy("compliance")}>
                  Compliance{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th onClick={() => sortBy("lastReportDate")}>
                  Last Report{" "}
                  <Icon
                    name="chevrons-up-down"
                    size={11}
                    className="sort-icon"
                  />
                </th>
                <th>Status</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((e) => (
                <tr key={e.id}>
                  <td className="card-hide">
                    <input
                      type="checkbox"
                      checked={isSelected(e.id)}
                      onChange={() => toggleSelect(e.id)}
                    />
                  </td>
                  <td data-label="Employee">
                    <div className="cell-emp">
                      <Avatar
                        name={e.name}
                        size="sm"
                        seed={e.id.length + e.name.length}
                      />
                      <div>
                        <div className="name">{e.name}</div>
                        <div className="sub">{e.email}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Department">{e.department}</td>
                  <td data-label="Role">
                    <span
                      className={`badge ${e.role === "Manager" ? "badge-primary" : e.role === "Lead" ? "badge-info" : "badge-muted"}`}
                    >
                      {e.role}
                    </span>
                  </td>
                  <td data-label="WhatsApp">
                    {e.whatsapp ? (
                      <span
                        style={{
                          color: "#25D366",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <Icon name="check-circle-2" size={14} /> Connected
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>—</span>
                    )}
                  </td>
                  <td data-label="Reports">
                    <strong>{e.reportsCount}</strong>
                  </td>
                  <td data-label="Compliance">
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          background: "var(--surface-3)",
                          borderRadius: 3,
                          overflow: "hidden",
                          maxWidth: 80,
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: e.compliance + "%",
                            background:
                              e.compliance >= 80
                                ? "var(--success)"
                                : e.compliance >= 60
                                  ? "var(--warning)"
                                  : "var(--error)",
                          }}
                        />
                      </div>
                      <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                        {e.compliance}%
                      </span>
                    </div>
                  </td>
                  <td data-label="Last Report">
                    {e.lastReportDate ? (
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: 500,
                          color: (() => {
                            const d = new Date(e.lastReportDate);
                            const now = new Date();
                            const diff = now - d;
                            if (diff < 864e5) return "var(--success)";
                            if (diff < 1728e5) return "var(--warning)";
                            return "var(--error)";
                          })(),
                        }}
                      >
                        {(() => {
                          const d = new Date(e.lastReportDate);
                          const now = new Date();
                          const diff = now - d;
                          if (diff < 6e4) return "Just now";
                          if (diff < 36e5) return `${Math.floor(diff / 6e4)}m ago`;
                          if (diff < 864e5) return `${Math.floor(diff / 36e5)}h ago`;
                          if (diff < 1728e5) return "Yesterday";
                          if (diff < 2592e6) return `${Math.floor(diff / 864e5)}d ago`;
                          return d.toLocaleDateString();
                        })()}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>No report</span>
                    )}
                  </td>
                  <td data-label="Status">
                    {e.status === "active" ? (
                      <span className="badge badge-success">
                        <span className="badge-dot" /> Active
                      </span>
                    ) : (
                      <span className="badge badge-muted">
                        <span className="badge-dot" /> Inactive
                      </span>
                    )}
                  </td>
                    <td data-label="">
                    <div className="row-actions">
                      <button
                        className="icon-btn"
                        onClick={() => openEditForm(e.id)}
                        title="Edit"
                      >
                        <Icon name="edit-2" size={14} />
                      </button>
                      <button
                        className="icon-btn"
                        onClick={() => openReports(e)}
                        title="View reports"
                      >
                        <Icon name="eye" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pages={pages}
            total={total}
            from={start}
            to={Math.min(start + pageSize, total)}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      ) : (
        <EmptyState
          title="No employees found"
          desc={
            employees.length
              ? "No employees match the current filter."
              : "Invite your first team member to start receiving daily reports."
          }
          icon="users"
          cta="Invite Employee"
          onCta={openInvite}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// EditEmployeeModal
// Proper state-managed edit form for employee details.
// ─────────────────────────────────────────────────────────────
function EditEmployeeModal({ employee, onClose, onSaved, toast }) {
  const [name, setName] = useState(employee.name);
  const [department, setDepartment] = useState(
    employee.department === "—" ? "" : employee.department || "",
  );
  const [phone, setPhone] = useState(employee.phone || "");
  const [isActive, setIsActive] = useState(employee.status === "active");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name) {
      toast("Name is required", "error");
      return;
    }
    setSaving(true);
    try {
      await apiUpdateEmployee(employee.id, { name, department, phone, isActive });
      toast("Employee updated", "success");
      onSaved();
    } catch (err) {
      toast(err.message || "Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${employee.name}? This cannot be undone.`)) return;
    try {
      await apiDeleteEmployee(employee.id);
      toast("Employee deleted", "success");
      onSaved();
    } catch (err) {
      toast(err.message || "Delete failed", "error");
    }
  };

  return (
    <>
      <div className="modal-head">
        <h3>Edit employee</h3>
        <button className="icon-btn" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="modal-body">
        <div className="form-row">
          <div className="field">
            <label>Full name</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
            />
          </div>
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              className="input"
              value={employee.email}
              disabled
              readOnly
            />
          </div>
        </div>
        <div className="form-row">
          <div className="field">
            <label>Phone (WhatsApp)</label>
            <input
              type="tel"
              className="input"
              value={phone}
              onChange={(ev) => setPhone(ev.target.value)}
            />
          </div>
          <div className="field">
            <label>Department</label>
            <input
              type="text"
              className="input"
              value={department}
              onChange={(ev) => setDepartment(ev.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="field">
            <label>Status</label>
            <select
              className="input"
              value={isActive ? "active" : "inactive"}
              onChange={(ev) => setIsActive(ev.target.value === "active")}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button
          className="btn btn-danger"
          style={{ marginRight: "auto" }}
          onClick={handleDelete}
          disabled={saving}
        >
          <Icon name="trash-2" size={16} /> Delete
        </button>
        <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Icon name="check" size={16} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// EmployeeReportsModal
// Lists all reports for one employee, filterable by week/month/year.
// Click a row → opens the full ReportModal.
// ─────────────────────────────────────────────────────────────
function EmployeeReportsModal({ emp, onClose, toast }) {
  const { openModal, closeModal } = useApp();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("month"); // week | month | year | all | ""
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    setLoading(true);
    listReports()
      .then(({ reports: rows }) => {
        setReports((rows || []).filter((r) => r.userId === emp.id));
      })
      .catch(() => toast("Failed to load reports", "error"))
      .finally(() => setLoading(false));
  }, [emp.id, toast]);

  const filtered = useMemo(() => {
    if (customFrom || customTo) {
      return reports.filter((r) => {
        const d = (r.date || "").slice(0, 10);
        if (customFrom && d < customFrom) return false;
        if (customTo && d > customTo) return false;
        return true;
      });
    }
    if (range === "all") return reports;
    const cutoff = new Date();
    if (range === "week") cutoff.setDate(cutoff.getDate() - 7);
    else if (range === "month") cutoff.setMonth(cutoff.getMonth() - 1);
    else if (range === "year") cutoff.setFullYear(cutoff.getFullYear() - 1);
    return reports.filter((r) => new Date(r.date) >= cutoff);
  }, [reports, range, customFrom, customTo]);

  const exportCsv = () => {
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Date", "Source", "Tasks", "Summary"];
    const rows = filtered.map((r) =>
      [
        new Date(r.date).toISOString().slice(0, 10),
        r.source || "web",
        (r.reportTasks || []).length,
        r.analysis?.summary || r.message || "",
      ].map(esc).join(","),
    );
    const csv = "﻿" + [header.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${emp.name.replace(/\s+/g, "-")}-reports.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openOne = async (r) => {
    try {
      const { report: full } = await getReportById(r.id);
      openModal(
        <ReportModal
          report={{
            ...full,
            employee: full.user,
            tasks: (full.reportTasks || []).length
              ? full.reportTasks.map((rt) => ({
                  text: rt.task?.title || "(task)",
                  status: rt.status,
                  note: rt.note,
                  source: rt.task?.source,
                  carried: !!rt.carriedFrom,
                }))
              : [{ text: full.message || "", status: "done" }],
            blockers: (full.reportTasks || [])
              .filter((rt) => rt.status === "pending" && rt.note)
              .map((rt) => rt.note),
            aiSummary: full.analysis?.summary || "AI summary pending...",
            sentiment: full.analysis?.sentiment || "neutral",
            source: full.source || "web",
            hoursWorked: 0,
          }}
          onClose={closeModal}
        />,
        "lg",
      );
    } catch (err) {
      toast(err.message || "Could not load report", "error");
    }
  };

  return (
    <>
      <div className="modal-head">
        <div>
          <h3>{emp.name}'s reports</h3>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {emp.email} · {filtered.length} report{filtered.length === 1 ? "" : "s"} in this range
          </div>
        </div>
        <button className="icon-btn" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <div
        className="modal-body"
        style={{ maxHeight: 520, overflowY: "auto", padding: 16 }}
      >
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
          {[
            ["week", "Past week"],
            ["month", "Past month"],
            ["year", "Past year"],
            ["all", "All time"],
          ].map(([k, l]) => (
            <button
              key={k}
              className={`filter-chip ${range === k && !customFrom && !customTo ? "active" : ""}`}
              onClick={() => { setRange(k); setCustomFrom(""); setCustomTo(""); }}
            >
              {l}
            </button>
          ))}
          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>or</span>
          <input type="date" value={customFrom}
            onChange={(e) => { setCustomFrom(e.target.value); setRange(""); }}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
          <input type="date" value={customTo}
            onChange={(e) => { setCustomTo(e.target.value); setRange(""); }}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }} />
        </div>
        {loading ? (
          <div style={{ color: "var(--text-muted)", padding: 16, fontSize: 13 }}>
            Loading reports…
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: 32 }}>
            <div className="ico">
              <Icon name="file-text" size={28} />
            </div>
            <h3 style={{ fontSize: 15 }}>No reports in this range</h3>
            <p style={{ fontSize: 12 }}>Try expanding the date range.</p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Date</th>
                <th>Source</th>
                <th>Tasks</th>
                <th>Summary</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td data-label="Date">{new Date(r.date).toLocaleDateString()}</td>
                  <td data-label="Source">
                    <span
                      className={`badge ${r.source === "whatsapp" ? "badge-success" : "badge-info"}`}
                    >
                      {r.source === "whatsapp" ? "WhatsApp" : "Web"}
                    </span>
                  </td>
                  <td data-label="Tasks">{(r.reportTasks || []).length}</td>
                  <td
                    data-label="Summary"
                    style={{
                      maxWidth: 280,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "var(--text-muted)",
                      fontSize: 12.5,
                    }}
                  >
                    {r.analysis?.summary || r.message || "—"}
                  </td>
                  <td data-label="">
                    <button
                      className="icon-btn"
                      onClick={() => openOne(r)}
                      title="View full report"
                    >
                      <Icon name="eye" size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="modal-foot" style={{ display: "flex", justifyContent: "space-between" }}>
        <button className="btn btn-ghost" onClick={exportCsv} disabled={filtered.length === 0}>
          <Icon name="download" size={14} /> Export CSV
        </button>
        <button className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  );
}
